// @givmo/app-admin — Settings → Payment Methods tab (07-1.3 + EP-WEB6). The company card-on-file
// surface PLUS the company-wallet slice (balance + deposit + set-default) the retired Flutter portal
// had (D-EP-1: BUILD — the React rebuild had not ported it).
//
// Three concerns on one tab:
//   1. COMPANY WALLET (EP-WEB6) — the balance card reads `GET /admin/company/wallet`
//      (`companiesGetCompanyWallet`). Visible to Member + Owner (backend `company:read`). Owner-only:
//      an "Add funds" affordance that opens the money-moving `DepositDialog` (charges a saved company
//      card via `POST /admin/company/wallet/deposit`). The dialog reads its own price for its
//      "Cover processing fees" row from the server's deposit quote (CAM-BE11).
//   2. CARDS ON FILE (07-1.3) — LIST via `companiesGetCompanyCardsV2` (`/v2` neutral surface); ADD via
//      the AddCardDialog (Shift4 tokenize → POST); DELETE behind the shared ConfirmDialog.
//   3. SET-DEFAULT (EP-WEB6) — the previously display-only "Default" badge is now real: an Owner-only
//      "Set as default" row action drives `PUT /admin/company/payment_method`
//      (`companiesUpdateCompanyPaymentMethod`, backend `company:edit`), which sets the wallet's
//      selected card; the neutral `/v2` cards surface then server-computes `is_default`, so
//      invalidating the cards + wallet queries reflects the new default.
//
// ROLE GATING — mirrors the BACKEND permission map (`permission_service.py`), which is also what the
// shipped iOS `CompanyPaymentMethodsScreen` encodes:
//   - Member + Owner: view balance + card list, ADD a card, REMOVE a card (`company_card:create/
//     list/delete` are MEMBER permissions — card management is deliberately not Owner-only).
//   - Deposit (`company_wallet:deposit`) and set-default (`company:edit_default_payment_method`;
//     hidden, not disabled, for anyone else): every Owner, and since EP-SUITE7 (D-EP-50) anyone the
//     console's Donations module makes its Owner — `mayAddFunds` / `maySetDefaultCard` from
//     `@givmo/employer-module`, the answer the Dashboard's Add funds gives too, so its door always
//     lands on the control.
// Whose deposits these are (CAM-R51): the signed-in person's Firebase uid, read from the auth
// context — never from the admin read, which can fail (cut 19's review F3) — and the company, from
// the admin read or else the wallet read. The deposit dialog holds a maybe-charged deposit's series
// under it beyond the tab, so a tab switch or a reload does not turn a re-entry into a second charge.
// The admin's role is read from `GET /admin/company/me` (`AdminOut.admin_company_role`, compared to
// 'Owner', and its `permissions`). One deliberate divergence from iOS: iOS FAILS CLOSED on an
// unknown/nil role (least privilege), while this tab FAILS OPEN on a role-read ERROR — the server 403
// on every mutating call is the authoritative backstop, so a transient role-read failure must never
// strand a real Owner (a slipped-through Member's deposit/set-default just errors server-side, moving
// no money).
//
// The root keeps `data-testid="settings-payments-tab"`.

import { use, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesDeleteCompanyCardMutation,
  companiesGetCompanyAdminOptions,
  companiesGetCompanyCardsV2Options,
  companiesGetCompanyCardsV2QueryKey,
  companiesGetCompanyWalletOptions,
  companiesGetCompanyWalletQueryKey,
  companiesUpdateCompanyPaymentMethodMutation,
} from '@givmo/api-client/query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  IconButton,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { CreditCard, Plus, Trash2, Wallet } from 'lucide-react';
import { mayAddFunds, maySetDefaultCard } from '@givmo/employer-module';
import { depositSeriesScope } from '@givmo/employer-module/features/settings/deposit-series';
import { AuthContext } from '../../auth/auth-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { AddCardDialog } from './add-card-dialog';
import { DepositDialog } from './deposit-dialog';
import { cardLabel, extractCards, type PaymentCard } from './payment-methods-decode';
import { toWalletView } from './wallet-decode';

const PREFIX = 'settings-payments';

const INTRO =
  'Manage your company wallet and the cards on file. Saved cards can be used to fund your company ' +
  'wallet and donation matching. Card details are entered securely through our payment provider and ' +
  'are never stored on Givmo’s servers.';

/**
 * The note for someone who may not do one of the two Owner acts here (EP-SUITE7): it names
 * exactly the act they lack, in the portal's own words. "Ask an Owner", never "Only": a Global
 * Admin can give either act to anyone (Customize, D-EP-50 (3a)) and the console can make a Member
 * an Owner of Donations, so "only company Owners can" would not be true. Holding both: no note.
 */
export function paymentMethodsNote(canAddFunds: boolean, canSetDefault: boolean): string | null {
  if (canAddFunds && canSetDefault) return null;
  const ask =
    !canAddFunds && !canSetDefault
      ? 'add funds or change the default card'
      : !canAddFunds
        ? 'add funds'
        : 'change the default card';
  return `Any company admin can add or remove cards. Ask an Owner to ${ask}.`;
}

export function SettingsPaymentMethodsTab() {
  // The current admin's role. A role-read ERROR fails OPEN (Owner-capable UI) — the server 403 is the
  // authoritative backstop, so a transient failure must never strand a real Owner.
  const roleQuery = useQuery(companiesGetCompanyAdminOptions());
  const role = roleQuery.data?.admin_company_role ?? null;
  // The signed-in uid names the deposit's series (CAM-R51, F3). Read without throwing: the tab is
  // also rendered outside the AuthProvider (its tests); with no uid the dialog keeps its own memory.
  const uid = use(AuthContext)?.user?.uid ?? null;

  if (roleQuery.isPending) {
    return (
      <div className="pt-16" {...testIdProp(`${PREFIX}-role-loading`)}>
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="mt-12 h-16 w-full" />
        <Skeleton className="mt-12 h-16 w-2/3" />
      </div>
    );
  }

  // Owner OR role-read error → the full surface (fail-open; see header). Anyone else → the cards,
  // plus each Owner act whose own string their permission set holds (EP-SUITE7).
  const standing = {
    settled: roleQuery.isSuccess,
    role,
    permissions: roleQuery.data?.permissions,
  };
  return (
    <PaymentMethods
      canAddFunds={mayAddFunds(standing)}
      canSetDefault={maySetDefaultCard(standing)}
      uid={uid}
      adminCompanyId={roleQuery.data?.admin_company ?? null}
    />
  );
}

function PaymentMethods({
  canAddFunds,
  canSetDefault,
  uid,
  adminCompanyId,
}: {
  canAddFunds: boolean;
  canSetDefault: boolean;
  uid: string | null;
  adminCompanyId: string | null;
}) {
  const queryClient = useQueryClient();

  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  // Whose deposits these are: the uid, and the company from the admin read or else the wallet read.
  const seriesScope = depositSeriesScope(uid, adminCompanyId ?? walletQuery.data?.domain_id);
  const cardsQuery = useQuery(companiesGetCompanyCardsV2Options());
  const cards = extractCards(cardsQuery.data);
  const note = paymentMethodsNote(canAddFunds, canSetDefault);

  const [addOpen, setAddOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PaymentCard | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    ...companiesDeleteCompanyCardMutation(),
    onSuccess: async () => {
      setPendingDelete(null);
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyCardsV2QueryKey() });
      toast.success('Card removed');
    },
    onError: (err) => {
      toast.error(isGivmoError(err) ? err.message : 'Could not remove the card. Please try again.');
    },
  });

  const setDefaultMutation = useMutation({
    ...companiesUpdateCompanyPaymentMethodMutation(),
    onSuccess: async () => {
      // `is_default` is server-computed from the wallet's selected card, and the wallet's
      // default_payment_method/card_id changed — invalidate BOTH so the badge + balance card refresh.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: companiesGetCompanyCardsV2QueryKey() }),
        queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() }),
      ]);
      toast.success('Default card updated');
    },
    onError: (err) => {
      // A 403 (a non-Owner who slipped through on a role-read error) or any other failure toasts.
      toast.error(
        isGivmoError(err) ? err.message : 'Could not update the default card. Please try again.',
      );
    },
    onSettled: () => setSettingDefaultId(null),
  });

  const confirmDelete = () => {
    if (pendingDelete === null) return;
    deleteMutation.mutate({ path: { card_id: pendingDelete.id } });
  };

  const setDefault = (card: PaymentCard) => {
    setSettingDefaultId(card.id);
    // A vaulted card is always a Credit Card source; the wallet's default is (payment_method, card_id).
    setDefaultMutation.mutate({ body: { payment_method: 'Credit Card', card_id: card.id } });
  };

  const onDeposited = async () => {
    setDepositOpen(false);
    await queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
    toast.success('Funds added to your company wallet');
  };

  // The dialog closed after a charge attempt WITHOUT a confirmed success (fatal error, or an
  // acknowledged maybe-charged close). Refresh the wallet so the balance the user is told to check
  // is fresh truth — a maybe-charged deposit may have actually landed.
  const onDepositCloseAfterAttempt = () => {
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
  };

  const hasCards = cards.length > 0;

  return (
    <div className="flex flex-col gap-16 pt-16" {...testIdProp(`${PREFIX}-tab`)}>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        {INTRO}
      </p>

      {/* ── Company wallet (EP-WEB6): balance + Owner-only deposit ── */}
      <Card>
        <CardBody className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-wallet`)}>
          <div className="flex items-center justify-between gap-16">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Company wallet
            </h2>
            {canAddFunds && (
              <div className="flex flex-col items-end gap-2">
                <Button
                  appearance="saas"
                  tone="primary"
                  size="sm"
                  leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                  disabled={!hasCards}
                  onClick={() => setDepositOpen(true)}
                  testId={`${PREFIX}-deposit`}
                >
                  Add funds
                </Button>
                {/* The add-a-card-first hint only once the cards query has RESOLVED empty — while it
                    is still loading we don't yet know there are no cards. */}
                {!hasCards &&
                  (cardsQuery.isPending ? (
                    <Skeleton className="h-12 w-56" testId={`${PREFIX}-deposit-hint-loading`} />
                  ) : (
                    <span
                      className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                      {...testIdProp(`${PREFIX}-deposit-hint`)}
                    >
                      Add a company card first.
                    </span>
                  ))}
              </div>
            )}
          </div>

          {walletQuery.isPending ? (
            <div {...testIdProp(`${PREFIX}-wallet-loading`)}>
              <Skeleton className="h-32 w-40" />
            </div>
          ) : walletQuery.isError ? (
            <div
              className="flex flex-col items-start gap-4"
              {...testIdProp(`${PREFIX}-wallet-error`)}
            >
              <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
                {isGivmoError(walletQuery.error)
                  ? walletQuery.error.message
                  : 'Failed to load your wallet balance.'}
              </p>
              <button
                type="button"
                onClick={() => void walletQuery.refetch()}
                className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                {...testIdProp(`${PREFIX}-wallet-error-retry`)}
              >
                Try again
              </button>
            </div>
          ) : (
            <WalletBalance wallet={walletQuery.data} />
          )}
        </CardBody>
      </Card>

      {/* ── Cards on file (07-1.3) — card management is Member + Owner (backend company_card:*) ── */}
      <Card>
        <CardBody className="flex flex-col gap-16">
          <div className="flex items-center justify-between gap-16">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Cards on file
            </h2>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              onClick={() => setAddOpen(true)}
              testId={`${PREFIX}-add`}
            >
              Add card
            </Button>
          </div>

          {note !== null && (
            <p
              className="font-sans text-s14 font-w400 tracking-normal text-text-tertiary"
              {...testIdProp(`${PREFIX}-member-note`)}
            >
              {note}
            </p>
          )}

          {cardsQuery.isPending ? (
            <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-loading`)}>
              <Skeleton className="h-[56px] w-full" />
              <Skeleton className="h-[56px] w-full" />
            </div>
          ) : cardsQuery.isError ? (
            <div className="flex flex-col items-start gap-4" {...testIdProp(`${PREFIX}-error`)}>
              <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
                {isGivmoError(cardsQuery.error)
                  ? cardsQuery.error.message
                  : 'Failed to load your cards.'}
              </p>
              {isGivmoError(cardsQuery.error) && supportReference(cardsQuery.error) !== null && (
                <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                  {supportReference(cardsQuery.error)}
                </p>
              )}
              <button
                type="button"
                onClick={() => void cardsQuery.refetch()}
                className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                {...testIdProp(`${PREFIX}-error-retry`)}
              >
                Try again
              </button>
            </div>
          ) : cards.length === 0 ? (
            <EmptyState
              testId={`${PREFIX}-empty-state`}
              icon={<Icon icon={CreditCard} size={20} aria-hidden />}
              title="No cards on file"
              description="Add a card to fund your company wallet and donation matching."
            />
          ) : (
            <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-list`)}>
              {cards.map((card) => (
                <CardRow
                  key={card.id}
                  card={card}
                  canSetDefault={canSetDefault}
                  deleting={deleteMutation.isPending && pendingDelete?.id === card.id}
                  settingDefault={setDefaultMutation.isPending && settingDefaultId === card.id}
                  onDelete={() => setPendingDelete(card)}
                  onSetDefault={() => setDefault(card)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <AddCardDialog open={addOpen} onOpenChange={setAddOpen} />

      {canAddFunds && (
        <DepositDialog
          open={depositOpen}
          onOpenChange={setDepositOpen}
          cards={cards}
          onDeposited={() => void onDeposited()}
          onCloseAfterAttempt={onDepositCloseAfterAttempt}
          seriesScope={seriesScope}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setPendingDelete(null);
        }}
        title="Remove this card?"
        subject={pendingDelete !== null ? cardLabel(pendingDelete) : undefined}
        body="This card will be removed from your company's saved payment methods. This cannot be undone."
        confirmLabel="Remove card"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        testId={`${PREFIX}-delete-confirm`}
      />
    </div>
  );
}

// ── The company wallet balance block: primary balance + (when a hold exists) an available/held line. ──
function WalletBalance({ wallet }: { wallet: Parameters<typeof toWalletView>[0] }) {
  const view = toWalletView(wallet);
  return (
    <div className="flex items-start gap-12">
      <Icon icon={Wallet} size={24} aria-hidden className="mt-2 text-text-secondary" />
      <div className="flex flex-col">
        <span className="font-sans text-s12 font-w600 uppercase tracking-givmo text-text-tertiary">
          Balance
        </span>
        <span
          className="font-sans text-s28 font-w700 tracking-givmo text-text-strong"
          {...testIdProp(`${PREFIX}-balance`)}
        >
          {view.balanceDisplay}
        </span>
        {view.hasHold && view.availableDisplay !== null && (
          <span
            className="mt-2 font-sans text-s13 font-w400 tracking-normal text-text-tertiary"
            {...testIdProp(`${PREFIX}-available`)}
          >
            {view.availableDisplay} available
            {view.heldDisplay !== null ? ` · ${view.heldDisplay} on hold` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ── One saved-card tile: brand + masked last4 + expiry, a default badge, a hover/focus-revealed
//    danger remove (Member + Owner), and "Set as default" on non-default cards for whoever may. ──
//
// ⚠ ON A TOUCH SCREEN THE REMOVE ICON IS ALWAYS SHOWN (EP-WEB42, as the console Wallet's): a phone or
// tablet has no hover, so `pointer-coarse` shows it at rest; a mouse keeps the reveal.
function CardRow({
  card,
  canSetDefault,
  deleting,
  settingDefault,
  onDelete,
  onSetDefault,
}: {
  card: PaymentCard;
  canSetDefault: boolean;
  deleting: boolean;
  settingDefault: boolean;
  onDelete: () => void;
  onSetDefault: () => void;
}) {
  return (
    <li
      className="group flex items-center justify-between gap-12 rounded-lg border border-border-subtle bg-surface px-16 py-12"
      {...testIdProp(`${PREFIX}-row-${card.id}`)}
    >
      <div className="flex min-w-0 items-center gap-12">
        <Icon icon={CreditCard} size={20} aria-hidden />
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-8">
            <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {cardLabel(card)}
            </span>
            {card.isDefault && (
              <Badge tone="neutral" testId={`${PREFIX}-row-${card.id}-default`}>
                Default
              </Badge>
            )}
          </div>
          {card.expiry !== null && (
            <span className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
              Expires {card.expiry}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-8">
        {canSetDefault && !card.isDefault && (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            loading={settingDefault}
            disabled={settingDefault}
            onClick={onSetDefault}
            testId={`${PREFIX}-row-${card.id}-set-default`}
          >
            Set as default
          </Button>
        )}
        <IconButton
          aria-label={`Remove ${cardLabel(card)}`}
          tone="danger"
          size="sm"
          disabled={deleting}
          onClick={onDelete}
          className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
          testId={`${PREFIX}-row-${card.id}-remove`}
        >
          <Icon icon={Trash2} size={16} aria-hidden />
        </IconButton>
      </div>
    </li>
  );
}
